import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { graphql, navigate } from 'gatsby'
import { css, Global } from '@emotion/react'
import styled from '@emotion/styled'
import Seo from '~components/Seo'
import Section from '~components/Section'
import DataViewer from '~utils/DataViewer'
import { useCart, useSiteState } from '~context/siteContext'
import { useLocation } from '@reach/router'
import { useQueryParams, StringParam } from 'use-query-params'
import RenderModule from '../components/RenderModule'
import RichText from '~components/RichText'
import Image from '~components/Image'
import Incrementer from '../components/Incrementer'
import AccordionSet from '../components/AccordionSet'
import Button from '../components/Button'
import MobileImageSlider from '../components/MobileImageSlider'
import FadeTransition from '~components/FadeTransition'
import { DarrSmall, Tick } from '~components/Svg'
import Overlay from '~components/Overlay'
import SlideDown from 'react-slidedown'
import { AnimatePresence, motion } from 'framer-motion'
import { Line } from '~components/Svg'
import { 
	mobile, 
	tablet, 
	OptionSummary,
	VariantIcon,
} from '~styles/global'
import { useRect } from 'react-use-rect'

const Product = ({ data }) => {
	const { title, content, options, optionSettings } = data.product
	const { addItem, openCart } = useCart()
	const [params] = useQueryParams({v: StringParam})
	const location = useLocation()
	const [selectedVariant, setSelectedVariant] = useState()
	const [selectedOptions, setSelectedOptions] = useState()
	const [localQty, setLocalQty] = useState(1)
	const [fragranceVisible, setFragranceVisible] = useState(false)
	const [miniCart, setMiniCart] = useState()
	const [barVisible, setBarVisible] = useState(false)
	const [barSticky, setBarSticky] = useState(false)
	const [stickyBarMobileRef, stickyBarMobileRect] = useRect({ scroll: true })
	const [endOfContentRef, endOfContentRect] = useRect({ scroll: true })

	const getValueData = (option, value) => {
		const setting = optionSettings.find(s => {
			const [o, v] = s.relatedOption.split(':')
			return o === option.name && v === value
		})
		return setting
	}

	const getSelectedValueIndex = (option) => {
		if(!selectedOptions) return ''
		return option.values.findIndex(val => {
			return selectedOptions[option.name] === val
		})
	}

	const add = qty => {
		addItem({
			...selectedVariant, 
			qty: qty,
			// We don't want to limit the maxQty if the policy is to continue selling
			maxQty: selectedVariant?.content.shopify.inventoryPolicy === 'continue' ? null : selectedVariant?.content.shopify.inventoryQuantity
		})
		if (miniCart) {
			setMiniCart(undefined)
			setTimeout(()=> (
				setMiniCart({
					selectedVariant: selectedVariant, 
					qty: qty
				})
			), 600)
		} else {
			setMiniCart({			
				selectedVariant: selectedVariant, 
				qty: qty
			})
		}
	}

	function navigateToVariant(variant){
		setSelectedVariant(variant)
		if(location.pathname.includes('/products/')){
			navigate(location.pathname + `${ variant ? `?v=${variant.content.shopify.variantId}` : ''}`,
				{ state: { disableScrollUpdate: true }, replace: true }
			)
		}
	}

	const findVariantByOptions = () => content.variants.find(v => {
		let match = true
		v.options.forEach(o => {
			if(o.value !== selectedOptions[o.name]) match = false
		})
		return match
	})

	const findVariantById = id => 
		content.variants.find(v => v.content.shopify.variantId.toString() === id)

	// Navigate to the new variant each time the options change
	useEffect(() => {
		if(selectedOptions){
			const newVariant = findVariantByOptions()
			navigateToVariant(newVariant)
			// Scroll to top in the middle of the image transition, when variant changes
			if(window.innerWidth > 768){
				window.setTimeout(() => {
					window.scrollTo({
						top: 0,
						left: 0,
					})
				}, 700)
			} 
		}
		// eslint-disable-next-line
	}, [selectedOptions])

	// Helper function to convert options array in to obejct
	const optionsToObject = opts => opts.reduce(function(map, obj) {
		map[obj.name] = obj.value
		return map
	}, {})

	// Set the variant to on load
	useLayoutEffect(() => {
		let newVariant
		if(!params.v){
			newVariant = content.variants[0]
		} else {
			newVariant = findVariantById(params.v)
		}
		if(newVariant?.options){
			setSelectedOptions(optionsToObject(newVariant.options))
		}
		if(newVariant){
			navigateToVariant(newVariant)
		}
		
		// eslint-disable-next-line
	}, [params.v])

	useEffect(() => {
		setFragranceVisible(false)
	}, [selectedVariant])

	const available = selectedVariant?.content.shopify.available

	useEffect(() => {
		setTimeout(()=> {setBarVisible(true)}, 200)
	}, [])

	return(
		<>
			<Seo 
				title={title}
				metaTitle={content?.seo?.metaTitle}
				description={content?.seo?.metaDescription}
				image={content?.seo?.socialImage}
			/>
			<Wrap>
				<StickyBar>
					<AnimatePresence exitBeforeEnter>
						<motion.div 
							key={barVisible}
							initial={{y: '100%' }}
							animate={{ y: ['100%', '-20%', '30%', '-15%', '10%', '-5%', '0%']}}
							exit={{y: '100%' }}
							transition={{
								type: 'spring',
								duration: 2.6,
								times: [0, 2, 2.2, 2.4, 2.46, 2.52, 2.56, 2.6]
							}}
						>	
							{barVisible && 
 							<AddToCartContainer>
 								<SelectedDetails>
 									<SelectedTitle>
 										{data?.product?.title}
 									</SelectedTitle>
 									{selectedOptions && 
										<VariantSummary>
											{Object.keys(selectedOptions).map(key => (
												<OptionSummary key={selectedVariant?.content?.shopify?.variantId + key}>
													<VariantIcon image={getValueData({name: key}, selectedOptions[key])?.swatch} aspectRatio={1} />
													<h3>{selectedOptions[key]}</h3>
												</OptionSummary>
											))}
										</VariantSummary>
 									}
 									<ProductIncrementer initialValue={1} maxValue={selectedVariant?.content?.shopify?.inventoryQuantity} onChange={val => setLocalQty(val)} />
 								</SelectedDetails>
 								<Right>
								 	{selectedVariant?.content?.saleMessage && 
									 <SaleMessage>{selectedVariant?.content?.saleMessage}</SaleMessage>
 									}
 									<Price className='numbers'>${parseInt(selectedVariant?.content?.shopify?.price)}</Price>
 									<PriceLine/>
 									<AddToCart 
 										onClick={available ? () => add(localQty) : null} 
 										className='h3'
 										noArrow={!available}
 										css={css`
											pointer-events: ${available ? 'all' : 'none'};
									`}>
 										{available ? 'Add to Bag' : 'Out of Stock'}
 									</AddToCart>
 								</Right>
 							</AddToCartContainer>
							}
						</motion.div>
					</AnimatePresence>
				</StickyBar>
				<Main>
					<MiniCarts css={css`${mobile}{transform: ${(stickyBarMobileRect?.top <= 0) && (endOfContentRect?.top >= 0) ?  'translateY(98px)': 'translateY(46px)'};}`}>
						<AnimatePresence exitBeforeEnter>
							<motion.div 
								key={miniCart}
								initial={{opacity: 0, y: '10px' }}
								animate={{opacity: 1, y: '0px' }}
								exit={{opacity: 0, y: '10px' }}
								transition={{type: 'spring', duration: 0.3}}
							>
								{miniCart && 
								<MiniCart
									variant={miniCart?.selectedVariant} 
									onClose={() => setMiniCart(undefined)}
									qty={miniCart?.qty}
								/>}
							</motion.div>
						</AnimatePresence>
					</MiniCarts>
					<MobileImageContainer>
						<MobileImages 
							images={selectedVariant?.content?.images} 
							totalImages={selectedVariant?.content?.images?.length}
						/>
					</MobileImageContainer>
					<Content>
						<Info>
							<Title>{title}</Title>
							<Measurements>{selectedVariant?.content?.dimensions}</Measurements>
							<Details><RichText content={content.description}/></Details>
							{options.map((o, i) => (
								<>
									{o.name === 'Fragrance' ? (
										<>
											{options.find(option => option.name === 'Fragrance') && 
											<ToggleFragrance onClick={() => setFragranceVisible(!fragranceVisible)}>
												<h3>
													Fragrance: <span dangerouslySetInnerHTML={{__html: `&#931${getSelectedValueIndex(o) + 2}`}}/> {selectedOptions?.Fragrance} <DarrSmall css={css`
														transform: ${fragranceVisible ? 'translateY(2px) rotate(180deg)' : 'translateY(2px)'};
													`}/>
												</h3>
											</ToggleFragrance>
											}
											<SlideDown css={css`transition: 0.4s ease-in-out;`}>
												{fragranceVisible &&
												<Fragrances>
													{o.values.map((value, i) => (
														<Fragrance key={value}>
															<button onClick={() => setSelectedOptions({...selectedOptions, [o.name]: value})}>
																<h3><span dangerouslySetInnerHTML={{__html: `&#931${i + 2}`}}/> {value}</h3>
																<p>
																	{getValueData(o, value)?.fragranceNotes.map(note => <span className="h3" key={note}>{note}<br/></span>)}
																	<Tick ticked={value === selectedOptions[o.name]} />
																</p>
															</button>
														</Fragrance>
													))}
												</Fragrances>
												}
											</SlideDown>
										</>
									) : (
										<Thumbnails>
											{o.values.map(value => (
												location.pathname.includes('/products/voucher') ?
													<VoucherTile 
														css={css`
															border-color: ${selectedVariant?.options[i]?.name === o?.name && selectedVariant?.options[i].value === value
													? 'var(--black)' : 'var(--beige)'}; 
													`}
														onClick={() => setSelectedOptions({...selectedOptions, [o.name]: value})}
														className='h4'
													>{value}</VoucherTile>
													:
													<Thumbnail 
														key={value} 
														onClick={() => setSelectedOptions({...selectedOptions, [o.name]: value})}
														css={css`
															${mobile}{
																h4{
																	opacity: ${selectedVariant?.options[i]?.name === o?.name && selectedVariant?.options[i].value === value ? '1' : '0'}; 
																}
															}
														`}
													>	
														<ThumbnailBorder 
															css={css`
															border-color: ${
												selectedVariant?.options[i]?.name === o?.name
																	&& selectedVariant?.options[i].value === value
													? 'var(--black)' : 'var(--beige)'}; 
															`}
														>
															<ThumbnailImage image={getValueData(o, value)?.thumb} aspectRatio={1}/>
														</ThumbnailBorder>
														<h4>{value}</h4>
													</Thumbnail>
												
											))}
										</Thumbnails>
									)
									}
								</>
							))}
							{selectedVariant?.content?.saleMessage && 
								<SaleMessageMobile>{selectedVariant?.content?.saleMessage}</SaleMessageMobile>
							}
						</Info>
						<ProductAccordion items={content.accordion}/>
					</Content>
					<DesktopImages>
						<FadeTransition integer={selectedVariant?.content?.shopify?.variantId}>
							{selectedVariant?.content?.images.map(image =>(
								<Image image={image} key={image.asset.id}/>
							))}
						</FadeTransition>
					</DesktopImages>
				</Main>
				<div ref={stickyBarMobileRef}/>
				<StickyBarMobile >
					<AddToCartContainer>
						<SelectedDetails>
							<SelectedTitle>
								{data?.product?.title}
							</SelectedTitle>
							{selectedOptions && 
								<VariantSummary>
									{Object.keys(selectedOptions).map(key => (
										<OptionSummary key={selectedVariant?.content?.shopify?.variantId + key}>
											<VariantIcon image={getValueData({name: key}, selectedOptions[key])?.swatch} aspectRatio={1} />
											<h3>{selectedOptions[key]}</h3>
										</OptionSummary>
									))}
								</VariantSummary>
							}
							<ProductIncrementer initialValue={1} maxValue={selectedVariant?.content?.shopify?.inventoryQuantity} onChange={val => setLocalQty(val)} />
						</SelectedDetails>
						<Right>
							{selectedVariant?.content?.saleMessage && 
							<SaleMessage>{selectedVariant?.content?.saleMessage}</SaleMessage>
							}
							<Price className='numbers'>${parseInt(selectedVariant?.content?.shopify?.price)}</Price>
							<PriceLine/>
							<AddToCart 
								onClick={available ? () => add(localQty) : null} 
								className='h3'
								noArrow={!available}
								css={css`
								pointer-events: ${available ? 'all' : 'none'};
							`}>
								{available ? 'Add to Bag' : 'Out of Stock'}
							</AddToCart>
						</Right>
					</AddToCartContainer>
				</StickyBarMobile>
				<ProductAccordionMobile items={content.accordion}/>
				{content.flexibleContent?.map((module, i) => (
					<RenderModule 
						key={module?._key} 
						module={module}
						css={css`
							margin-bottom: ${content.flexibleContent.length === i + 1 ? '-1px' : '0px'};
						`}
					/>
				))}	
				<div ref={endOfContentRef}/>
			</Wrap>
			<DataViewer data={data} name="data"/>
		</>
	)
}
const Wrap = styled.div`
	position: relative;
	margin-bottom: 57px;
	${tablet}{
		margin-bottom: 51px;
	}
`
const MiniCarts = styled.div`
	position: fixed;
	top: 54px;
	right: 0;
	z-index: 3;
	display: grid;
	transition: all 0.3s;
	${mobile}{
		left: 0;
		top: 0;
	}
`
const MiniCart = styled(Overlay)`
	margin: var(--xxs);
	transition: opacity 0.3s;
`
const Main = styled(Section)`
	border-bottom: 1px solid var(--black);
	position: relative;
	min-height: 100vh;
	>div{
		grid-template-rows: 1fr max-content;
	}
	${mobile}{
		border-bottom: 0px;
		min-height: auto;
	}
`
const MobileImages = styled(MobileImageSlider)`
	display: none;
	grid-column: span 6;
	${mobile}{
		display: block;
	}
` 
const Content = styled.div`
	grid-column: 1/7;
	margin-left: -40px;
	margin-right: -40px;
	column-gap: 40px;
	padding: 0 40px;
	box-sizing: border-box;
	display: grid;
	grid-template-rows: 1fr max-content;
	height: calc(100vh - 112px);
	position: sticky;
	top: 54px;
	overflow: scroll;
	&::-webkit-scrollbar {
		display: none;
	}
	scrollbar-width: none;
	-ms-overflow-style: none;
	${tablet}{
		height: calc(100vh - 108px);
	}
	${mobile}{
		grid-column: span 6;
		margin-left: -20px;
		margin-right: -20px;
		column-gap: 20px;
		padding: 0 20px;
		height: auto;
	}
`
const Info = styled.div`
	max-width: 520px;
	margin: 0 auto;
	align-self: center;
	padding: var(--xl) 0;
	flex-grow: 1;
	text-align: center;
	${mobile}{
		margin-bottom: var(--xxxl);
		padding: 0;
	}
`
const Title = styled.h1`
	text-align: center;
	margin-bottom: var(--l);
	${mobile}{
		margin-bottom: var(--m);
	}
`
const Measurements = styled.h3`
	margin-bottom: var(--l);
	text-align: center;
`
const Details = styled.h3`
	text-align: center;
	margin: 0 auto;
	max-width: 430px;
`
const ProductAccordion = styled(AccordionSet)`
	margin: 0 -40px;
	${mobile}{
		display: none;
	}
`
const ProductAccordionMobile = styled(AccordionSet)`
	display: none;
	${mobile}{
		display: block;
		border-bottom: 1px solid var(--black);
		>div{
			&:first-of-type{
				border-top: none;
			}
		}
	}
`
const DesktopImages = styled.div`
	border-left: 1px solid var(--black);
	grid-column: 7/13;
	margin-right: -40px;
	${mobile}{
		display: none;
	}
`
const StickyBar = styled.div`
	position: sticky;
	left: 0;
	right: 0;
	z-index: 2;
	height: 0;
	transition: all 0.2s;
	top: calc(100vh - 55px);
	${tablet}{
		top: calc(100vh - 51px);
	}
	${mobile}{
		display: none;
	}
`
const StickyBarMobile = styled(StickyBar)`
	display: none;
	${mobile}{
		display: block;
		grid-column: span 6;
		top: calc(0vh + 46px);
		height: auto;
	}
`
const AddToCartContainer = styled.div`
	background: var(--beige);
	display: flex;
	border-top: 1px solid var(--black);
	border-bottom: 1px solid var(--black);
	align-items: center;
	justify-content: space-between;
	padding: var(--xs) var(--l);
	z-index: 1;
`
const SelectedDetails = styled.div`
	justify-self: start;
	display: flex;
	align-items: center;
`
const SelectedTitle = styled.h3`
	margin-right: var(--xl);
`
const ProductIncrementer = styled(Incrementer)`
	height: 36px;
	width: 68px;
`
const Right = styled.div`
	margin-left: auto;
	display: flex;
	align-items: center;
`
const SaleMessage = styled.h4`
	margin-right: var(--l);
	margin-left: var(--l);
	padding: calc(var(--xxs) + 2px) var(--xs) var(--xxs);
	border: 1px solid var(--black);
	${mobile}{
		display: none;
	}
`
const SaleMessageMobile = styled(SaleMessage)`
	display: none;
	margin-right: auto;
	margin-left: auto;
	margin-top: var(--l);
	${mobile}{
		display: inline-block;
	}
`
const Price = styled.h3`
	margin-right: var(--xxs);
	line-height: 17px;
	${tablet}{
		margin-top: 2px;
	}
	${mobile}{
		margin-top: 0px;
	}
`
const PriceLine = styled(Line)`
	height: 2px; 
	width: 18px;
	margin-right: var(--xxs);
	margin-top: -3px;
	${tablet}{
		margin-top: -1px;
	}
	${mobile}{
		margin-top: -2px;
	}
`
const AddToCart = styled(Button)`
	svg{
		${tablet}{
			top: 2px;
		}
		${mobile}{
			top: 1px;
		}
	}
`
const Thumbnails = styled.div`
	display: flex;
	justify-content: center;
	margin-top: var(--l);
`
const Thumbnail = styled.button`
	display: grid;
	margin-right: var(--m);
	&:last-of-type{
		margin-right: 0px;
	}
	${mobile}{
		h4{
			opacity: 0;
			transition: opacity 0.3s;
		}
	}
`
const ThumbnailBorder = styled.div`
	width: 53px;
	margin: 0 auto var(--s);
	border-radius: 50%;
	border: 1px solid;
	transition: border-color 0.3s;
	padding: 3px;
	align-self: center;
	${mobile}{
		width: 38px;
	}
`
const VoucherTile = styled.button`
	border: 1px solid;
	margin-right: var(--s);
	padding: calc(var(--xxs) + 2px) var(--xs) var(--xxs);
	&:last-of-type{
		margin-right: 0px;
	}
`
const ThumbnailImage = styled(Image)`
	border-radius: 50%;
	overflow: hidden;
`
const MobileImageContainer = styled.div`
	grid-column: span 6;
`

const VariantSummary = styled.div`
	margin-right: var(--xl);
	display: flex;
  gap: var(--xl);
	${mobile}{
		display: none;
	}
`

const Fragrances = styled.div`
	display: flex;
	align-items: start;
	margin: 0 auto;
	padding-top: var(--m);
	text-align: center;
	p{
		margin-top: var(--s);
		line-height: 1.1;
	}
	span{
		line-height: 1;
	}
	${mobile}{
		display: block;
	}
`

const Fragrance = styled.div`
	width: 160px;
	padding: 0 10px;
	svg{
		width: 13px;
		margin-top: var(--xs);
	}
	${mobile}{
		margin: 0 auto var(--m);
		&:last-child{
			margin-bottom: 0;
		}
		svg{
			width: 12px;
		}
	}
`

const ToggleFragrance = styled.button`
	text-align: center;
	margin: var(--l) auto 0;
	display: block;
	svg{
		width: 13px;
		transition: transform 0.4s;
	}
	${mobile}{
		svg{
			width: 12px;
		}
	}
`

export const query = graphql`
	query ProductQuery($slug: String) {
		product: sanityProduct(slug: {current: {eq: $slug}}) {
			title
			slug{
				current
			}
			options {
				name
				values
			}
			optionSettings {
				relatedOption
				swatch {
					...imageWithAlt
				}
				thumb{
					...imageWithAlt
				}
				fragranceNotes
			}
			content {
				description: _rawDescription(resolveReferences: {maxDepth: 4})
				accordion{
					title
					text: _rawText
				}
				flexibleContent{
					... on SanityArticleRow {
						...articleRow
					}
					... on SanityCarousel {
						...carousel
					}
					... on SanityNumberedGrid {
						...numberedGrid
					}
					... on SanityTextImage {
						...textImage
					}
					... on SanityTextImages {
						...textImages
					}
					... on SanityImageSection {
						...imageSection
					}
					... on SanityFeaturedArticle {
						...featuredArticle
					}
					... on SanityReviews {
						...reviews
					}
					... on SanityProductRow {
						...productRow
					}
					... on SanityProductCatalogue {
						...productCatalogue
					}
				}
				variants {
					_type
					content {
						images {
							...imageWithAlt
						}
						thumb{
							...imageWithAlt
						}
						dimensions
						saleMessage
						shopify {
							price
							variantId
							variantTitle
							inventoryQuantity
							inventoryPolicy
							available
							productSlug
							productTitle
						}
					}
					options {
						name
						value
					}
				}
			}
		}
	}
`

const VariantSelector = styled.form`
	max-width: 300px;
	padding: 30px 0;
	grid-column: 1 / 3;
`

Product.propTypes = {
	data: PropTypes.object,
	daat: PropTypes.object, 
}

export default Product